import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { fetchApi } from "../../common/api";
import { supabase } from "../../common/supabase";
import { State } from "../state";
import { Memory } from "./types";

export enum MEMORIES_ACTION {
  LIST_MEMORIES_ACTION = "LIST_MEMORIES_ACTION",
  UPDATE_MEMORY = "UPDATE_MEMORY",
  UPDATE_MEMORIES = "UPDATE_MEMORIES",
  DELETE_MEMORY = "DELETE_MEMORY",
}

export interface ListMemoriesAction {
  type: MEMORIES_ACTION.LIST_MEMORIES_ACTION;
  memories: Memory[];
}

export const listMemoriesAction = (userId: string) => {
  return async (
    dispatch: ThunkDispatch<State, unknown, AnyAction>,
    getState: () => State,
  ) => {
    if (getState().memories.hasLoaded) {
      return;
    }

    const memories = await fetchApi<Memory[]>("/memories/list", "POST", {
      user_id: userId,
    });

    const action: ListMemoriesAction = {
      type: MEMORIES_ACTION.LIST_MEMORIES_ACTION,
      memories,
    };
    dispatch(action);
    return action;
  };
};

export interface UpdateMemoryAction {
  type: MEMORIES_ACTION.UPDATE_MEMORY;
  memory: Partial<Memory> & { id: string };
}

export interface UpdateMemoriesAction {
  type: MEMORIES_ACTION.UPDATE_MEMORIES;
  memories: (Partial<Memory> & { id: string })[];
}

export const fetchAndUpdateMemoryAction = (
  memoryId: string,
  options: { force?: boolean; requireTranscript?: boolean } = {},
) => {
  return async (
    dispatch: ThunkDispatch<State, unknown, AnyAction>,
    getState: () => State,
  ) => {
    const existingMemory = getState().memories.items[memoryId];
    const hasRequiredTranscript =
      !options.requireTranscript ||
      Boolean(existingMemory?.is_processing) ||
      (existingMemory?.transcript !== null &&
        existingMemory?.transcript !== undefined);

    if (!options.force && existingMemory && hasRequiredTranscript) {
      return;
    }

    const memory = await fetchApi<Memory>("/memories/get", "POST", {
      id: memoryId,
    });
    dispatch({ type: MEMORIES_ACTION.UPDATE_MEMORY, memory });
  };
};

export const fetchMemoryAudioUrlAction = (memoryId: string) => {
  return async (
    dispatch: ThunkDispatch<State, unknown, AnyAction>,
    getState: () => State,
  ) => {
    const existingAudioUrl = getState().memories.items[memoryId]?.audioUrl;
    if (existingAudioUrl?.startsWith("blob:")) {
      return;
    }

    const path = `${memoryId}/audio.m4a`;
    const { data, error } = await supabase.storage
      .from("memories")
      .download(path);

    if (error || !data) {
      throw error ?? new Error(`Unable to download memory audio: ${path}`);
    }

    const audioBlob = data.type
      ? data
      : data.slice(0, data.size, "audio/mp4");

    dispatch({
      type: MEMORIES_ACTION.UPDATE_MEMORY,
      memory: {
        id: memoryId,
        audioUrl: URL.createObjectURL(audioBlob),
      },
    });
  };
};

export const updateMemoryTitleAction = (memoryId: string, name: string) => {
  return async (
    dispatch: ThunkDispatch<State, unknown, AnyAction>,
  ): Promise<Memory> => {
    const memory = await fetchApi<Memory>("/memories/update_title", "POST", {
      memory_id: memoryId,
      name,
    });

    dispatch({
      type: MEMORIES_ACTION.UPDATE_MEMORY,
      memory,
    });

    return memory;
  };
};

export interface DeleteMemoryAction {
  type: MEMORIES_ACTION.DELETE_MEMORY;
  memoryId: string;
}

export const deleteMemoryAction = (memoryId: string) => {
  return async (dispatch: ThunkDispatch<State, unknown, AnyAction>) => {
    await fetchApi("/memories/delete", "POST", {
      memory_id: memoryId,
    });

    dispatch({
      type: MEMORIES_ACTION.DELETE_MEMORY,
      memoryId,
    });
  };
};
